import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Copy, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const CopySnippet = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative group bg-ink rounded-2xl p-5 my-4">
      <pre className="text-green-300/80 text-xs font-mono leading-relaxed whitespace-pre-wrap">{text}</pre>
      <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${copied ? "bg-green-500 text-white" : "bg-white/10 text-white/70 hover:bg-brand hover:text-white"}`}>
        {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
      </button>
    </div>
  );
};

const TIPS = [
  {
    num: "01",
    title: "Lead with Production Impact, Not Research Scores",
    body: `Hiring managers at Google, Meta, and Amazon care about one thing: what you shipped and what happened after. Academic metrics like "achieved 94.2% accuracy on CIFAR-10" mean nothing without production context.

Instead, frame every ML project around deployment:`,
    snippet: `Before: "Trained a BERT model achieving F1 score of 0.91 on NLP benchmark dataset"
After:  "Deployed fine-tuned BERT model to production serving 50K daily API requests, \n        reducing manual review load by 65% for the content moderation team"`,
    after: null,
  },
  {
    num: "02",
    title: "Show Your Stack Depth, Not Just Breadth",
    body: `Don't write a laundry list of frameworks. Signal depth by showing what you built with each tool — and at what scale.`,
    snippet: `Weak:  "PyTorch, TensorFlow, Scikit-learn, Keras, HuggingFace, MLflow, Kubeflow"
Strong: Languages:  Python, SQL, Scala
        Frameworks: PyTorch (production DL), HuggingFace Transformers, Scikit-learn
        MLOps:      MLflow, Kubeflow, DVC, Docker, AWS SageMaker
        Data:       Spark, BigQuery, Airflow`,
    after: null,
  },
  {
    num: "03",
    title: "Quantify Model Performance in Business Terms",
    body: `Raw metrics tell engineers something. Translated metrics tell hiring managers everything. Always pair a model metric with a business outcome.`,
    snippet: `Pattern: "[Model result] → [Business outcome]"

Examples:
• "Reduced false positive rate from 18% to 4.2% → cut customer escalations by 30%"
• "Improved recommendation CTR by 12% → attributed to $2.4M additional annual revenue"
• "Reduced model training time from 14 hours to 3.5 hours → enabled 2x faster iteration cycles"`,
    after: null,
  },
  {
    num: "04",
    title: "Highlight Data Engineering Work",
    body: `AI/ML roles at top companies expect you to handle your own data pipelines. If you've built ETL pipelines, feature stores, or data preprocessing systems — put it front and center.`,
    snippet: `"Designed and maintained Airflow DAG pipeline processing 8TB of user event data daily, \nreducing data freshness lag from 24 hours to 90 minutes for downstream model training"`,
    after: null,
  },
  {
    num: "05",
    title: "Address the 'Research vs. Engineering' Gap Directly",
    body: `For ML roles at product companies (not research labs), they want engineers who can build reliable ML systems — not just train models in notebooks. Your resume should make this clear.

Signal production maturity by mentioning:`,
    snippet: `✓ Model monitoring and drift detection (e.g., Evidently, Arize, WhyLabs)
✓ A/B testing and experiment tracking (e.g., MLflow, Weights & Biases)
✓ Serving infrastructure (e.g., TorchServe, TF Serving, FastAPI + Docker)
✓ Versioning and reproducibility (DVC, Git LFS, model registries)
✓ Cost optimization (model compression, quantization, distillation)`,
    after: null,
  },
  {
    num: "06",
    title: "One Resume Per Role Family",
    body: `An ML Engineering resume and an Applied Scientist resume should emphasize different things. Keep two versions:

- **ML Engineer version**: Systems, pipelines, latency, scale, infrastructure
- **Applied Scientist version**: Research contributions, novel approaches, publications, experimentation

Sending a single generic resume to both is the most common mistake ML candidates make.`,
    snippet: null,
    after: null,
  },
  {
    num: "07",
    title: "The AI Prompt to Fix Your ML Resume",
    body: `Use this targeted prompt to optimize specifically for ML/AI roles:`,
    snippet: `I'm a machine learning engineer targeting [ML Engineer / Applied Scientist / AI Engineer] roles at product-led tech companies. My experience includes [brief 2-sentence summary].

Please audit my resume with a focus on:
1. Are my ML results framed in business impact, not just model metrics?
2. Is my tech stack presented by depth (not just listed)?
3. Are there any signals that suggest "research-only" experience vs. production ML?
4. What keywords from this JD [paste JD] are completely missing?
5. Does my summary immediately signal I can build reliable production systems?

[Paste resume here]`,
    after: null,
  },
];

export default function BlogPostAIMLResumePage() {
  return (
    <div data-testid="blog-aiml-page" className="bg-white">
      <SEOHead
        title="AI/ML Resume Tips for Engineers | ApplyStrat Blog"
        description="7 specific resume tips for ML engineers and AI specialists targeting roles at Google, Meta, Amazon, and other top tech companies."
        canonical="https://applystrat.com/blog/aiml-resume-tips"
      />

      <section className="pt-36 pb-12 text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.10) 0%, transparent 60%)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-ink-secondary text-sm hover:text-ink transition-colors mb-8">
              <ArrowLeft size={14} /> Back to Blog
            </Link>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">AI/ML</span>
              <span className="text-ink-secondary text-xs">7 min read</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-medium tracking-tighter text-ink mb-5 leading-tight">
              7 Things Every <span className="text-brand">AI/ML Resume</span><br />Needs in 2025
            </h1>
            <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">
              Most ML resumes look the same. Here's what separates candidates who get calls from Google and Meta — from those who don't.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <motion.p {...fadeUp()} className="text-ink-secondary leading-relaxed text-base mb-4">
            The AI/ML job market is highly competitive but the resumes are surprisingly similar — generic model metrics, framework dumps, and zero production context. Hiring managers at top companies make their decision in 10 seconds. These 7 tips will make those 10 seconds count.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 bg-surface-raised">
        <div className="max-w-2xl mx-auto px-6 pt-4">
          <div className="space-y-10">
            {TIPS.map((tip, i) => (
              <motion.div key={tip.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease, delay: 0.05 }}
                data-testid={`aiml-tip-${i + 1}`}
                className="bg-white rounded-3xl p-8 shadow-sm border border-black/5">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-medium text-brand/20">{tip.num}</span>
                </div>
                <h2 className="text-ink font-medium text-lg mb-4">{tip.title}</h2>
                <div className="text-ink-secondary text-sm leading-relaxed whitespace-pre-line">{tip.body}</div>
                {tip.snippet && <CopySnippet text={tip.snippet} />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl font-medium text-ink mb-4">Want the Full Resume Rewrite?</h2>
            <p className="text-ink-secondary text-base mb-8">Use our 9-step master prompt for a complete audit, language rewrite, and skills restructure tailored to your target role.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/resume-optimizer">Try the Master Prompt <ArrowRight size={16} /></Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to="/apply">Work With ApplyStrat</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
